/**
 * Role-Based Access Control (RBAC) system
 * Following WordPress-style permission hierarchy with per-user grants
 * 
 * PERMISSION MODEL:
 * 1. Role-based: Each role has a base set of permissions (ROLE_PERMISSIONS)
 * 2. Per-user grants: Individual users can be granted additional permissions
 *    via the `grantedPermissions` field on their User document
 * 
 * EDGE CASES HANDLED:
 * - Stale grants: Use isValidPermission() to filter out renamed/removed permissions
 * - Ownership checks: Granted _OWN permissions still require resource ownership
 * - Role changes: Grants are additive; if role already has permission, grant is redundant (harmless)
 * - Empty grants: undefined or [] both handled gracefully
 * 
 * USAGE:
 * - Client-side: usePermissions() hook (uses AuthContext.checkPermission)
 * - Server-side: PermissionChecker.hasPermissionWithGrants()
 * - Admin UI: Grant permissions via user management dashboard
 */

// User Roles (hierarchical - each role inherits permissions from lower roles)
// owner: Highest role with full system access except collections/tokens
// admin: Can manage collections and tokens (exclusive)
// editor: Can publish and manage all content
// writer: FROZEN — community-writer path is parked; kept for existing writer docs only.
//         Do NOT assign 'writer' to new users (see auth-service first-login default).
// contributor: user-world default for NEW Twitter logins. Submits signals/answers via the
//         /contribute console; has NO editorial/CMS/admin perms and never reaches /admin.
//         Sits BELOW writer in the hierarchy.
// member: a contributor who has PROVEN ownership of an entity (WS-K L3 claim → verify). Same
//         user-world capability set as contributor (answer/right-of-reply, edit own profile);
//         the ONLY added power is that a member's answer on an entity they own is asserted
//         `human:member:<uid>`. THE COI LINE: a member can POINT, never ASSERT — no verdict,
//         no admin, never staff. Sits ABOVE contributor, BELOW writer.
export type UserRole = 'contributor' | 'member' | 'writer' | 'editor' | 'admin' | 'owner';

// Granular Permissions
export enum Permission {
  // Article permissions
  ARTICLE_CREATE = 'article_create',
  ARTICLE_READ_OWN = 'article_read_own',
  ARTICLE_READ_ALL = 'article_read_all',
  ARTICLE_EDIT_OWN = 'article_edit_own',
  ARTICLE_EDIT_ALL = 'article_edit_all',
  ARTICLE_DELETE_OWN = 'article_delete_own',
  ARTICLE_DELETE_ALL = 'article_delete_all',
  ARTICLE_PUBLISH_OWN = 'article_publish_own',
  ARTICLE_PUBLISH_ALL = 'article_publish_all',
  ARTICLE_SUBMIT_FOR_REVIEW = 'article_submit_for_review', // Submit articles for editor/admin review
  ARTICLE_ASSIGN = 'article_assign', // Assign articles to other users
  
  // Breaking news permissions (mirrors article permissions)
  BREAKING_NEWS_CREATE = 'breaking_news_create',
  BREAKING_NEWS_READ_OWN = 'breaking_news_read_own',
  BREAKING_NEWS_READ_ALL = 'breaking_news_read_all',
  BREAKING_NEWS_EDIT_OWN = 'breaking_news_edit_own',
  BREAKING_NEWS_EDIT_ALL = 'breaking_news_edit_all',
  BREAKING_NEWS_DELETE_OWN = 'breaking_news_delete_own',
  BREAKING_NEWS_DELETE_ALL = 'breaking_news_delete_all',
  BREAKING_NEWS_PUBLISH_OWN = 'breaking_news_publish_own',
  BREAKING_NEWS_PUBLISH_ALL = 'breaking_news_publish_all',
  BREAKING_NEWS_SUBMIT_FOR_REVIEW = 'breaking_news_submit_for_review', // Submit breaking news for review
  BREAKING_NEWS_ASSIGN = 'breaking_news_assign', // Assign breaking news to writers
  
  // Media library permissions
  MEDIA_UPLOAD = 'media_upload',
  MEDIA_READ_OWN = 'media_read_own',
  MEDIA_READ_ALL = 'media_read_all',
  MEDIA_EDIT_OWN = 'media_edit_own',
  MEDIA_EDIT_ALL = 'media_edit_all',
  MEDIA_DELETE_OWN = 'media_delete_own',
  MEDIA_DELETE_ALL = 'media_delete_all',
  
  // Media folder permissions
  MEDIA_FOLDER_CREATE = 'media_folder_create',
  MEDIA_FOLDER_EDIT = 'media_folder_edit',
  MEDIA_FOLDER_DELETE = 'media_folder_delete',
  MEDIA_FOLDER_MOVE_ITEMS = 'media_folder_move_items',
  
  // User management permissions
  USER_CREATE = 'user_create',
  USER_READ_ALL = 'user_read_all',
  USER_READ_PROFILES = 'user_read_profiles', // Read-only access to user profiles/stats
  USER_EDIT_OWN = 'user_edit_own',
  USER_EDIT_ALL = 'user_edit_all',
  USER_DELETE = 'user_delete',
  USER_CHANGE_ROLES = 'user_change_roles',
  USER_ACTIVATE_DEACTIVATE = 'user_activate_deactivate',
  
  // Category management permissions
  CATEGORY_CREATE = 'category_create',
  CATEGORY_READ = 'category_read',
  CATEGORY_EDIT = 'category_edit',
  CATEGORY_DELETE = 'category_delete',
  
  // Dashboard and analytics permissions
  DASHBOARD_ACCESS = 'dashboard_access',
  ANALYTICS_VIEW = 'analytics_view',
  SITE_CONFIG_VIEW = 'site_config_view',
  SITE_CONFIG_EDIT = 'site_config_edit',
  
  // Comment moderation permissions
  COMMENT_MODERATE = 'comment_moderate',
  
  // Navigation and UI permissions
  ADMIN_MENU_ACCESS = 'admin_menu_access', // For collections/tokens (admin only)
  OWNER_MENU_ACCESS = 'owner_menu_access', // For owner-level features
  EDITOR_MENU_ACCESS = 'editor_menu_access',
  
  // Asset management permissions (admin-exclusive)
  COLLECTIONS_MANAGE = 'collections_manage',
  TOKENS_MANAGE = 'tokens_manage',
  
  // Advertising management (admin-exclusive)
  ADS_MANAGE = 'ads_manage',

  // ── Contributor (user-world) permissions ──────────────────────────────────
  // These back the /contribute console. They are NOT editorial/CMS perms — a
  // signal/answer is a contribution to the entity graph, human-reviewed before it
  // affects anything, and grants the contributor zero access to /admin.
  SIGNAL_SUBMIT = 'signal_submit', // Submit a signal (a reading, not an assertion) to the board
  OPEN_QUESTION_ANSWER = 'open_question_answer', // Answer an agent-raised open question
  PROFILE_EDIT_OWN = 'profile_edit_own', // Edit own user-world contributor profile
}

// Contributor (user-world) base permissions.
// Deliberately MINIMAL: submit signals/answers + edit own profile. NOTHING here grants
// dashboard/admin access, article creation, or any editorial/CMS capability. The
// staff roles below (writer/editor/admin/owner) do NOT inherit these — they're a
// separate user-world capability set, so a contributor can never traverse into /admin.
const CONTRIBUTOR_PERMISSIONS = [
  Permission.SIGNAL_SUBMIT,
  Permission.OPEN_QUESTION_ANSWER,
  Permission.PROFILE_EDIT_OWN,
];

// Member (WS-K L3) base permissions — the SAME user-world set as contributor. A member is a
// contributor who has proven entity ownership; ownership only changes their ASSERTER namespace on
// the owned entity (right-of-reply, derived server-side in the answer route), NOT their
// capabilities. DELIBERATELY identical to CONTRIBUTOR_PERMISSIONS: no admin/editor/writer perm,
// and (THE COI LINE) NO entity-verdict permission — none exists, and none is added here. A member
// can answer open questions (right-of-reply) + edit their own profile, nothing more.
const MEMBER_PERMISSIONS = [
  Permission.SIGNAL_SUBMIT,
  Permission.OPEN_QUESTION_ANSWER,
  Permission.PROFILE_EDIT_OWN,
];

// Base permissions for each role (without inheritance)
const WRITER_PERMISSIONS = [
  // Writer can only manage their own content
  Permission.ARTICLE_CREATE,
  Permission.ARTICLE_READ_OWN,
  Permission.ARTICLE_EDIT_OWN,
  Permission.ARTICLE_DELETE_OWN,
  Permission.ARTICLE_SUBMIT_FOR_REVIEW, // Writers can submit articles for editor/admin review
  // REMOVED: Writers cannot publish - only create drafts for editor/admin approval
  
  // Breaking news - writers can create and submit for review (same as articles)
  Permission.BREAKING_NEWS_CREATE,
  Permission.BREAKING_NEWS_READ_OWN,
  Permission.BREAKING_NEWS_EDIT_OWN,
  Permission.BREAKING_NEWS_DELETE_OWN,
  Permission.BREAKING_NEWS_SUBMIT_FOR_REVIEW, // Writers can submit breaking news for review
  // REMOVED: Writers cannot publish breaking news - only create drafts for editor/admin approval
  
  // Media library - only their own uploads
  Permission.MEDIA_UPLOAD,
  Permission.MEDIA_READ_OWN,
  Permission.MEDIA_EDIT_OWN,
  Permission.MEDIA_DELETE_OWN,
  
  // Profile management
  Permission.USER_EDIT_OWN,
  
  // Basic read access
  Permission.CATEGORY_READ,
  Permission.DASHBOARD_ACCESS,
];

const EDITOR_ADDITIONAL_PERMISSIONS = [
  // Additional editor permissions
  Permission.ARTICLE_READ_ALL,
  Permission.ARTICLE_EDIT_ALL,
  Permission.ARTICLE_DELETE_ALL,
  Permission.ARTICLE_PUBLISH_OWN, // Editors can publish their own articles
  Permission.ARTICLE_PUBLISH_ALL, // Editors can publish any article
  Permission.ARTICLE_ASSIGN, // Assign articles to writers
  
  // Breaking news - editors can publish and manage all
  Permission.BREAKING_NEWS_READ_ALL,
  Permission.BREAKING_NEWS_EDIT_ALL,
  Permission.BREAKING_NEWS_DELETE_ALL,
  Permission.BREAKING_NEWS_PUBLISH_OWN, // Editors can publish their own breaking news
  Permission.BREAKING_NEWS_PUBLISH_ALL, // Editors can publish any breaking news
  Permission.BREAKING_NEWS_ASSIGN, // Assign breaking news to writers
  
  // Media library - can read/edit all media, but only delete own
  Permission.MEDIA_READ_ALL,
  Permission.MEDIA_EDIT_ALL,
  // Note: Editors can only delete their own media (inherited from writer)
  
  // Media folder management
  Permission.MEDIA_FOLDER_CREATE,
  Permission.MEDIA_FOLDER_EDIT,
  Permission.MEDIA_FOLDER_DELETE,
  Permission.MEDIA_FOLDER_MOVE_ITEMS,
  
  // REMOVED: USER_READ_PROFILES - editors don't need direct user access
  // They get author info from articles/breaking-news and tag community pages

  // Content moderation
  Permission.COMMENT_MODERATE,
  
  // UI access
  Permission.EDITOR_MENU_ACCESS,
  
  // Analytics view
  Permission.ANALYTICS_VIEW,
];

const ADMIN_ADDITIONAL_PERMISSIONS = [
  // Collections, Tokens, Tags, and Carousel management
  Permission.COLLECTIONS_MANAGE,
  Permission.TOKENS_MANAGE,
  
  // Advertising management
  Permission.ADS_MANAGE,
  
  // Media - admins can delete anyone's media
  Permission.MEDIA_DELETE_ALL,
  
  // Category management
  Permission.CATEGORY_CREATE,
  Permission.CATEGORY_EDIT,
  Permission.CATEGORY_DELETE,
  
  // User management (read-only for admins, full access for owners)
  Permission.USER_READ_ALL,
  
  // Admin UI access
  Permission.ADMIN_MENU_ACCESS,
];

const OWNER_ADDITIONAL_PERMISSIONS = [
  // Owner has ALL admin permissions PLUS:
  ...ADMIN_ADDITIONAL_PERMISSIONS,
  
  // User management (owner-exclusive)
  Permission.USER_CREATE,
  Permission.USER_READ_ALL,
  Permission.USER_EDIT_ALL,
  Permission.USER_DELETE,
  Permission.USER_CHANGE_ROLES,
  Permission.USER_ACTIVATE_DEACTIVATE,
  
  // Site configuration (owner-exclusive)
  Permission.SITE_CONFIG_VIEW,
  Permission.SITE_CONFIG_EDIT,
  
  // Owner UI access
  Permission.OWNER_MENU_ACCESS,
];

// Role-based permission mappings with proper inheritance.
// NOTE: contributor is a SEPARATE user-world set — the staff chain (writer→owner) does
// NOT inherit contributor perms, and contributor does NOT inherit any staff perms. This
// keeps the user-world (contribute) and staff-world (admin) capability sets disjoint.
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  contributor: CONTRIBUTOR_PERMISSIONS,
  member: MEMBER_PERMISSIONS,
  writer: WRITER_PERMISSIONS,
  editor: [...WRITER_PERMISSIONS, ...EDITOR_ADDITIONAL_PERMISSIONS],
  admin: [...WRITER_PERMISSIONS, ...EDITOR_ADDITIONAL_PERMISSIONS, ...ADMIN_ADDITIONAL_PERMISSIONS],
  owner: [...WRITER_PERMISSIONS, ...EDITOR_ADDITIONAL_PERMISSIONS, ...OWNER_ADDITIONAL_PERMISSIONS],
};

// Resource ownership context
export interface ResourceContext {
  ownerId?: string; // The user ID who owns the resource
  authorId?: string; // For articles, the author ID
  uploadedBy?: string; // For media, who uploaded it
}

// Permission check context
export interface PermissionContext {
  user: {
    id: string;
    role: UserRole;
    isActive: boolean;
  };
  resource?: ResourceContext;
}

// Utility functions for permission checking
export class PermissionChecker {
  /**
   * Check if a role has a specific permission
   */
  static hasPermission(role: UserRole, permission: Permission): boolean {
    return ROLE_PERMISSIONS[role].includes(permission);
  }
  
  /**
   * Check if a user has permission to perform an action on a resource
   */
  static canPerformAction(
    context: PermissionContext,
    permission: Permission
  ): boolean {
    const { user, resource } = context;
    
    // User must be active
    if (!user.isActive) return false;
    
    // Check if role has the base permission
    if (!this.hasPermission(user.role, permission)) return false;
    
    // Handle ownership-based permissions
    if (resource) {
      return this.checkResourceOwnership(user, permission, resource);
    }
    
    return true;
  }
  
  /**
   * Check ownership-based permissions
   * Public for use with per-user granted permissions
   */
  static checkResourceOwnership(
    user: { id: string; role: UserRole },
    permission: Permission,
    resource: ResourceContext
  ): boolean {
    // Normalize IDs for comparison
    const normalizedUserId = typeof user.id === 'string' ? user.id.trim() : user.id;
    const normalizedOwnerId = typeof resource.ownerId === 'string' ? resource.ownerId?.trim() : resource.ownerId;
    const normalizedAuthorId = typeof resource.authorId === 'string' ? resource.authorId?.trim() : resource.authorId;
    const normalizedUploadedBy = typeof resource.uploadedBy === 'string' ? resource.uploadedBy?.trim() : resource.uploadedBy;
    
    const isOwner = normalizedOwnerId === normalizedUserId || 
                   normalizedAuthorId === normalizedUserId || 
                   normalizedUploadedBy === normalizedUserId;
    
    // Owner-only permissions
    const ownerOnlyPermissions = [
      Permission.ARTICLE_READ_OWN,
      Permission.ARTICLE_EDIT_OWN,
      Permission.ARTICLE_DELETE_OWN,
      Permission.ARTICLE_PUBLISH_OWN,
      Permission.BREAKING_NEWS_READ_OWN,
      Permission.BREAKING_NEWS_EDIT_OWN,
      Permission.BREAKING_NEWS_DELETE_OWN,
      Permission.BREAKING_NEWS_PUBLISH_OWN,
      Permission.MEDIA_READ_OWN,
      Permission.MEDIA_EDIT_OWN,
      Permission.MEDIA_DELETE_OWN,
    ];
    
    if (ownerOnlyPermissions.includes(permission)) {
      return isOwner;
    }
    
    // All-access permissions (editors and admins)
    const allAccessPermissions = [
      Permission.ARTICLE_READ_ALL,
      Permission.ARTICLE_EDIT_ALL,
      Permission.ARTICLE_DELETE_ALL,
      Permission.ARTICLE_PUBLISH_ALL,
      Permission.BREAKING_NEWS_READ_ALL,
      Permission.BREAKING_NEWS_EDIT_ALL,
      Permission.BREAKING_NEWS_DELETE_ALL,
      Permission.BREAKING_NEWS_PUBLISH_ALL,
      Permission.MEDIA_READ_ALL,
      Permission.MEDIA_EDIT_ALL,
      Permission.MEDIA_DELETE_ALL,
    ];
    
    if (allAccessPermissions.includes(permission)) {
      // These permissions override ownership requirements
      return true;
    }
    
    return true;
  }
  
  /**
   * Get all permissions for a role
   */
  static getRolePermissions(role: UserRole): Permission[] {
    return ROLE_PERMISSIONS[role];
  }
  
  /**
   * Check if role can access admin areas
   */
  static canAccessAdmin(role: UserRole): boolean {
    return this.hasPermission(role, Permission.ADMIN_MENU_ACCESS);
  }
  
  /**
   * Check if role can access editor areas
   */
  static canAccessEditor(role: UserRole): boolean {
    return this.hasPermission(role, Permission.EDITOR_MENU_ACCESS) ||
           this.hasPermission(role, Permission.ADMIN_MENU_ACCESS);
  }
  
  /**
   * Get the minimum role required for a permission
   */
  static getMinimumRoleForPermission(permission: Permission): UserRole | null {
    for (const role of ['contributor', 'member', 'writer', 'editor', 'admin', 'owner'] as UserRole[]) {
      if (this.hasPermission(role, permission)) {
        return role;
      }
    }
    return null;
  }
  
  /**
   * Check if role can access owner areas
   */
  static canAccessOwner(role: UserRole): boolean {
    return this.hasPermission(role, Permission.OWNER_MENU_ACCESS);
  }

  /**
   * Check if user has permission via role OR granted permissions
   * Use this for server-side checks where you have the full user object
   */
  static hasPermissionWithGrants(
    role: UserRole,
    permission: Permission,
    grantedPermissions?: string[]
  ): boolean {
    // Check role-based permission first
    if (this.hasPermission(role, permission)) {
      return true;
    }
    
    // Then check granted permissions
    return grantedPermissions?.includes(permission) ?? false;
  }

  /**
   * Validate a permission string is a valid Permission enum value
   * Use this to clean up stale grants when permissions are renamed/removed
   */
  static isValidPermission(permissionString: string): boolean {
    return Object.values(Permission).includes(permissionString as Permission);
  }

  /**
   * Filter out invalid permissions from a grants array
   * Returns only valid Permission enum values
   */
  static filterValidPermissions(grantedPermissions?: string[]): Permission[] {
    if (!grantedPermissions) return [];
    return grantedPermissions.filter(
      (p): p is Permission => this.isValidPermission(p)
    );
  }

  /**
   * Get all grantable permissions for a role
   * These are permissions the role doesn't already have that could be granted
   */
  static getGrantablePermissions(role: UserRole): Permission[] {
    const rolePermissions = this.getRolePermissions(role);
    return Object.values(Permission).filter(p => !rolePermissions.includes(p));
  }
}

// Action types for common operations
export interface ArticleAction {
  type: 'create' | 'read' | 'edit' | 'delete' | 'publish';
  articleId?: string;
  authorId?: string;
}

export interface MediaAction {
  type: 'upload' | 'read' | 'edit' | 'delete';
  mediaId?: string;
  uploadedBy?: string;
}

export interface UserAction {
  type: 'create' | 'read' | 'edit' | 'delete' | 'change_role' | 'activate';
  targetUserId?: string;
}

// Helper function to convert actions to permissions
export function getPermissionForAction(
  action: ArticleAction | MediaAction | UserAction,
  scope: 'own' | 'all' = 'own'
): Permission {
  if ('articleId' in action) {
    // Article action
    switch (action.type) {
      case 'create': return Permission.ARTICLE_CREATE;
      case 'read': return scope === 'own' ? Permission.ARTICLE_READ_OWN : Permission.ARTICLE_READ_ALL;
      case 'edit': return scope === 'own' ? Permission.ARTICLE_EDIT_OWN : Permission.ARTICLE_EDIT_ALL;
      case 'delete': return scope === 'own' ? Permission.ARTICLE_DELETE_OWN : Permission.ARTICLE_DELETE_ALL;
      case 'publish': return scope === 'own' ? Permission.ARTICLE_PUBLISH_OWN : Permission.ARTICLE_PUBLISH_ALL;
      default:
        throw new Error(`Unknown article action type: ${(action as any).type}`);
    }
  } else if ('mediaId' in action) {
    // Media action
    switch (action.type) {
      case 'upload': return Permission.MEDIA_UPLOAD;
      case 'read': return scope === 'own' ? Permission.MEDIA_READ_OWN : Permission.MEDIA_READ_ALL;
      case 'edit': return scope === 'own' ? Permission.MEDIA_EDIT_OWN : Permission.MEDIA_EDIT_ALL;
      case 'delete': return scope === 'own' ? Permission.MEDIA_DELETE_OWN : Permission.MEDIA_DELETE_ALL;
      default:
        throw new Error(`Unknown media action type: ${(action as any).type}`);
    }
  } else {
    // User action
    switch (action.type) {
      case 'create': return Permission.USER_CREATE;
      case 'read': return Permission.USER_READ_ALL;
      case 'edit': return Permission.USER_EDIT_ALL;
      case 'delete': return Permission.USER_DELETE;
      case 'change_role': return Permission.USER_CHANGE_ROLES;
      case 'activate': return Permission.USER_ACTIVATE_DEACTIVATE;
      default:
        throw new Error(`Unknown user action type: ${action.type}`);
    }
  }
}
